import { defineStore } from 'pinia';
import { getCached, setCached } from '@/utils/catalogCache';
import { fetchAllItems } from '@/utils/listResponse';
import api from '@/axios';

export const useSeguroStore = defineStore('seguro', {
  state: () => ({
    // 0) Catálogos precargados
    tiposDomicilios: [],
    tiposMediosContacto: [],
    bancos: [],
    instrumentos: [],
    organizaciones: [],
    unidades: [],
    enrollers: [],
    // 1) Contratante — nombres alineados con WizardPolizasDto
    rfc: '',
    curp: '',
    fechaNacimiento: '',
    edad: '',
    nombre: '',
    paterno: '',
    materno: '',
    contratanteId: '',
    informacionPersonalId: null,
    disabledRFC: 1,
    // Dirección (objetos con campos alineados al DTO: tipoId, numeroExt, numeroInt)
    domicilios: [],
    domicilioFiscalIgual: true,
    disabledCP: 1,
    direccionUnidad: null,
    // Medios de contacto (objetos con tipoMedioId, descripcion)
    mediosContacto: [],
    // Financieros (objetos con instrumentoId, bancoId)
    informacionFinanciera: [],
    // Póliza
    polizaNumero: '',
    polizaId: '',
    polizaVersionId: '',
    version: 1,
    folio: '',
    status: '',
    etapaWizard: 0,
    notas: '',
    observaciones: '',
    fechaSolicitud: '',

    deducible: '',
    periodoPago: '',
    inicioVigencia: '',
    finVigencia: '',
    // Derecho de póliza: lo define el backend por año (se consulta con la fecha de solicitud).
    derechoPoliza: null,
    derechoPolizaAnio: null,
    derechoPolizaEstado: 'idle', // 'idle' | 'cargando' | 'ok' | 'sin-monto' | 'error'
    derechoPolizaMensaje: '',
    // IVA vigente (16 %), el mismo para GMM y Vida; fijo en esta versión.
    iva: 0.16,
    sumaPrimas: 0,
    sumaAsegurada: 100000000,
    coaseguro: 0,
    primaPeriodica: 0,
    primaTotal: 0,
    notasOrdenDeTrabajo: '',
    observacionesInternas: '',
    poliza: '',
    montoIva: 0,
    diasContratados: '',
    topeCoaseguro: 0,
    conversionGarantizada: null,
    coberturasSeleccionadas: [],

    // Laborales
    informacionLaboralId: null,
    organizacionId: '',
    unidadId: '',
    lugarTrabajoId: '',
    referencia: '',
    enrolerId: '',
    // Producto — IDs alineados con el DTO
    aseguradoras: [],
    ramos: [],
    plazos: [],
    divisas: [],
    productos: [],
    tipos: [],
    aseguradoraId: '',
    aseguradoraNombre: '',
    productoId: '',
    productoNombre: '',
    ramoId: '',
    plazoId: '',
    tipoId: '',
    divisaId: '',
    // Asegurados
    asegurados: [],
    // Flags UI para cascada de selects
    disabledAseguradora: 1,
    disabledRamo: 1,
    disabledPlazo: 1,
    disabledTipo: 1,
    disabledDivisa: 1,
    disabledProducto: 1,
    // Tarifas y deducibles
    productoDetalle: null,
    tarifas: [],
    deducibles: [],
    // Snapshot generado al confirmar cotización
    cotizacionSnapshot: null,
  }),

  actions: {
    // ── Derecho de póliza ──────────────────────────────────────────────────────
    resetDerechoPoliza() {
      this.derechoPoliza = null;
      this.derechoPolizaAnio = null;
      this.derechoPolizaEstado = 'idle';
      this.derechoPolizaMensaje = '';
    },
    // Consulta el derecho de póliza del año de la fecha de solicitud (no el del año en curso).
    async cargarDerechoPoliza() {
      // El año sale del texto de la fecha (AAAA-MM-DD o ISO) para no depender de la zona horaria.
      const anio = /^\d{4}-\d{2}-\d{2}/.test(String(this.fechaSolicitud)) ? Number(String(this.fechaSolicitud).slice(0, 4)) : NaN;
      if (!Number.isInteger(anio)) {
        this.resetDerechoPoliza();
        return;
      }
      if (this.derechoPolizaEstado === 'ok' && this.derechoPolizaAnio === anio) return;
      this.derechoPolizaEstado = 'cargando';
      this.derechoPolizaMensaje = '';
      this.derechoPoliza = null;
      try {
        const { data } = await api.get(`derechos-poliza/${anio}`);
        this.derechoPoliza = data.importe;
        this.derechoPolizaAnio = anio;
        this.derechoPolizaEstado = 'ok';
      } catch (err) {
        this.derechoPolizaAnio = null;
        if (err?.response?.status === 404) {
          this.derechoPolizaEstado = 'sin-monto';
          this.derechoPolizaMensaje = `No hay derecho de póliza registrado para ${anio}.`;
        } else {
          this.derechoPolizaEstado = 'error';
          this.derechoPolizaMensaje = 'No se pudo consultar el derecho de póliza.';
        }
      }
    },

    // ── Catálogos ──────────────────────────────────────────────────────────────
    async fetchTiposDomicilios() {
      const cached = getCached('cat_v2_tiposdomicilios');
      if (cached) { this.tiposDomicilios = cached; return; }
      try {
        this.tiposDomicilios = (await fetchAllItems("tiposdomicilios")).items;
        setCached('cat_v2_tiposdomicilios', this.tiposDomicilios);
      } catch {
        this.error = 'No se pudieron cargar los Tipos de Domicilios.';
      }
    },
    async fetchTiposMedios() {
      const cached = getCached('cat_v2_tiposmedios');
      if (cached) { this.tiposMediosContacto = cached; return; }
      try {
        this.tiposMediosContacto = (await fetchAllItems("tiposmedios")).items;
        setCached('cat_v2_tiposmedios', this.tiposMediosContacto);
      } catch {
        this.error = 'No se pudieron cargar los Tipos de Medios de Contacto.';
      }
    },
    async fetchBancos() {
      const cached = getCached('cat_v2_bancos');
      if (cached) { this.bancos = cached; return; }
      try {
        this.bancos = (await fetchAllItems("bancos")).items;
        setCached('cat_v2_bancos', this.bancos);
      } catch {
        this.error = 'No se pudieron cargar los Bancos.';
      }
    },
    async fetchInstrumentos() {
      const cached = getCached('cat_v2_instrumentos');
      if (cached) { this.instrumentos = cached; return; }
      try {
        this.instrumentos = (await fetchAllItems("instrumentos")).items;
        setCached('cat_v2_instrumentos', this.instrumentos);
      } catch {
        this.error = 'No se pudieron cargar los Instrumentos.';
      }
    },
    async fetchOrganizaciones() {
      const cached = getCached('cat_v2_organizaciones');
      if (cached) { this.organizaciones = cached; return; }
      try {
        const { items } = await fetchAllItems("organizaciones");
        this.organizaciones = items.map(item => ({ id: item.id, nombre: item.nombre }));
        setCached('cat_v2_organizaciones', this.organizaciones);
      } catch {
        this.error = 'No se pudieron cargar las Organizaciones.';
      }
    },
    async fetchUnidades() {
      const cached = getCached('cat_v2_unidades');
      if (cached) { this.unidades = cached; return; }
      try {
        const { items } = await fetchAllItems("unidades");
        this.unidades = items.map(item => ({ id: item.id, nombre: item.nombre, organizacionId: item.organizacionId }));
        setCached('cat_v2_unidades', this.unidades);
      } catch {
        this.error = 'No se pudieron cargar las Unidades.';
      }
    },
    async fetchEnrollers() {
      const cached = getCached('cat_v2_enrollers');
      if (cached) { this.enrollers = cached; return; }
      try {
        const { items } = await fetchAllItems("usuarios/rol/enroller");
        this.enrollers = items.map(item => ({ id: item.id, nombre: item.informacionPersonal.nombre }));
        setCached('cat_v2_enrollers', this.enrollers);
      } catch {
        this.error = 'No se pudieron cargar los Enrollers.';
      }
    },

    // ── Reset ──────────────────────────────────────────────────────────────────
    resetContratante() {
      this.resetGenerales();
      this.resetDomicilio();
      this.resetMediosContacto();
      this.resetInformacionFinanciera();
      this.resetAsegurados();
    },
    resetForm() {
      if (confirm("Seguro que deseas reiniciar el formulario.")) {
        this.resetContratante();
      }
    },
    // Limpia solo lo del contratante (no la póliza): se usa al buscar otro RFC para no mezclar
    // datos de dos contratantes (specs/007-wizard-etapa-contratante, FR-010).
    reiniciarContratante() {
      this.curp = '';
      this.fechaNacimiento = '';
      this.edad = '';
      this.nombre = '';
      this.paterno = '';
      this.materno = '';
      this.disabledRFC = 1;
      this.contratanteId = '';
      this.informacionPersonalId = null;
      this.resetDomicilio();
      this.resetMediosContacto();
      this.resetInformacionFinanciera();
    },
    resetGenerales() {
      this.rfc = '';
      this.curp = '';
      this.fechaNacimiento = '';
      this.nombre = '';
      this.paterno = '';
      this.materno = '';
      this.disabledRFC = 1;
      this.contratanteId = '';
      this.informacionPersonalId = null;
      this.polizaNumero = '';
      this.polizaId = '';
      this.polizaVersionId = '';
      this.version = 1;
      this.folio = '';
      this.status = '';
      this.etapaWizard = 0;
      this.notas = '';
      this.observaciones = '';
      this.informacionLaboralId = null;
      this.cotizacionSnapshot = null;
    },
    resetDomicilio() {
      this.domicilios = [];
      this.domicilioFiscalIgual = true;
      this.disabledCP = 1;
      this.direccionUnidad = null;
    },
    resetMediosContacto() {
      this.mediosContacto = [];
    },
    resetInformacionFinanciera() {
      this.informacionFinanciera = [];
    },
    resetAsegurados() {
      this.asegurados = [];
    },

    // ── Helpers de nombres ────────────────────────────────────────────────────
    updateAseguradoraNombre() {
      if (!this.aseguradoraId || !this.aseguradoras?.length) {
        this.aseguradoraNombre = '';
        return;
      }
      const found = this.aseguradoras.find(a => a.id == this.aseguradoraId);
      this.aseguradoraNombre = found ? found.nombre : this.aseguradoraId;
    },
    updateProductoNombre() {
      if (!this.productoId || !this.productos?.length) {
        this.productoNombre = '';
        return;
      }
      const found = this.productos.find(p => p.id == this.productoId);
      this.productoNombre = found ? found.name : this.productoId;
    },

    // ── WizardPolizasDto ──────────────────────────────────────────────────────
    // Después de homologar los nombres del store con el DTO, este método es
    // casi una proyección directa; no hay tabla de traducción.
    buildWizardDto(etapa) {
      const lugarTrabajoNombre = this.lugarTrabajoId == 1 ? 'Secretaría de Salud'
        : this.lugarTrabajoId == 2 ? 'Otro' : null;
      const ramoNombre = (this.ramos || []).find(r => r.id == this.ramoId)?.nombre || null;
      const _plazoFound = (this.plazos || []).find(p => p.id == this.plazoId);
      const plazoNombre = _plazoFound != null ? String(_plazoFound.plazo ?? _plazoFound.nombre ?? '') || null : null;
      const tipoNombre = (this.tipos || []).find(t => t.id == this.tipoId)?.nombre || null;
      const divisaFound = (this.divisas || []).find(d => d.id == this.divisaId);
      const divisaNombre = divisaFound?.nombre || null;
      const divisaSimbolo = divisaFound?.simbolo || null;
      const enrolerNombre = (this.enrollers || []).find(e => e.id == this.enrolerId)?.nombre || null;

      return {
        versionDTO: 1,
        etapaWizard: etapa,
        polizaId: this.polizaId || null,
        numeroPoliza: this.poliza || this.polizaNumero || null,
        polizaVersionId: this.polizaVersionId || null,
        version: 1,
        folio: this.folio || null,
        status: etapa,
        contratanteId: this.contratanteId || null,
        notas: this.notasOrdenDeTrabajo || this.notas || null,
        observaciones: this.observacionesInternas || this.observaciones || null,
        informacionPersonalId: this.informacionPersonalId || null,
        rfc: this.rfc || null,
        curp: this.curp || null,
        fechaNacimiento: this.fechaNacimiento || null,
        edad: this.edad || null,
        nombre: this.nombre || null,
        paterno: this.paterno || null,
        materno: this.materno || null,
        domicilios: (this.domicilios || []).map(d => ({
          domicilioId: d.domicilioId || null,
          tipoId: d.tipoId || 1,
          calle: d.calle || null,
          numeroExt: d.numeroExt || null,
          numeroInt: d.numeroInt || null,
          codigoPostal: d.codigoPostal || null,
          codigoPostalId: d.codigoPostalId || null,
          colonia: d.colonia || null,
          ciudad: d.ciudad || null,
          municipio: d.municipio || null,
          municipioId: d.municipioId || null,
          estado: d.estado || null,
          estadoId: d.estadoId || null,
          paisId: d.paisId || 1,
        })),
        mediosContacto: (this.mediosContacto || []).map(m => ({
          medioId: m.medioId || null,
          tipoMedioId: m.tipoMedioId || null,
          descripcion: m.descripcion || null,
        })),
        informacionFinanciera: (this.informacionFinanciera || []).map(f => ({
          informacionFinancieraId: f.informacionFinancieraId || null,
          instrumentoId: f.instrumentoId || null,
          bancoId: f.bancoId || null,
          cuenta: f.cuenta || null,
        })),
        informacionLaboralId: this.informacionLaboralId || null,
        lugarTrabajoId: this.lugarTrabajoId || null,
        lugarTrabajo: lugarTrabajoNombre,
        organizacionId: this.organizacionId || null,
        unidadId: this.unidadId || null,
        referencia: this.referencia || null,
        productoId: this.productoId || null,
        producto: this.productoNombre || null,
        divisaId: this.divisaId || null,
        divisa: divisaNombre,
        divisaSimbolo: divisaSimbolo,
        tipoId: this.tipoId || null,
        tipo: tipoNombre,
        plazoId: this.plazoId || null,
        plazo: plazoNombre,
        ramoId: this.ramoId || null,
        ramo: ramoNombre,
        aseguradoraId: this.aseguradoraId || null,
        aseguradora: this.aseguradoraNombre || null,
        enrolerId: this.enrolerId || null,
        enroler: enrolerNombre,
        fechaSolicitud: this.fechaSolicitud || null,
        coaseguro: this.coaseguro || null,
        topeCoaseguro: this.topeCoaseguro || null,
        deducible: this.deducible || null,
        asegurados: (this.asegurados || []).map(a => ({
          aseguradoId: a.aseguradoId || null,
          parentesco: a.parentesco || null,
          generoId: a.genero === 'Masculino' ? 1 : a.genero === 'Femenino' ? 2 : null,
          genero: a.genero || null,
          fechaNacimiento: a.fechaNacimiento || null,
          edad: a.edad || null,
          paisId: a.paisId || null,
          pais: a.paisNombre || null,
          estadoId: a.estadoId || null,
          estado: (a.estadosNacimiento || []).find(e => e.id == a.estadoId)?.nombre || null,
          nombre: a.nombre || null,
          paterno: a.paterno || null,
          materno: a.materno || null,
          antiguedad: a.antiguedad || null,
          fechaAlta: a.fechaAlta || null,
          fechaFin: a.fechaFin || null,
          diasContratados: a.diasContratados || null,
        })),
      };
    },

    // ── Carga desde DTO (edición de póliza existente) ─────────────────────────
    loadFromDto(dto) {
      // Metadatos de póliza
      this.polizaId = dto.polizaId || '';
      this.polizaNumero = dto.numeroPoliza || '';
      this.polizaVersionId = dto.polizaVersionId || '';
      this.version = dto.version || 1;
      this.folio = dto.folio || '';
      this.status = dto.status || '';
      this.etapaWizard = dto.etapaWizard || 0;
      this.notas = dto.notas || '';
      this.observaciones = dto.observaciones || '';
      this.contratanteId = dto.contratanteId || '';
      this.informacionPersonalId = dto.informacionPersonalId || null;
      this.informacionLaboralId = dto.informacionLaboralId || null;

      // Información personal
      this.rfc = dto.rfc || '';
      this.curp = dto.curp || '';
      this.fechaNacimiento = dto.fechaNacimiento || '';
      this.edad = dto.edad || '';
      this.nombre = dto.nombre || '';
      this.paterno = dto.paterno || '';
      this.materno = dto.materno || '';

      // Domicilios
      this.domicilios = (dto.domicilios || []).map(d => ({
        domicilioId: d.domicilioId || null,
        tipoId: d.tipoId || 1,
        codigoPostal: d.codigoPostal || '',
        codigoPostalId: d.codigoPostalId || null,
        estado: d.estado || '',
        estadoId: d.estadoId || null,
        municipio: d.municipio || '',
        municipioId: d.municipioId || null,
        colonia: d.colonia || '',
        calle: d.calle || '',
        ciudad: d.ciudad || '',
        paisId: d.paisId || 1,
        numeroExt: d.numeroExt || '',
        numeroInt: d.numeroInt || '',
        disabledCP: d.codigoPostal ? 0 : 1,
      }));
      this.domicilioFiscalIgual = !this.domicilios.some(d => d.tipoId === 3);

      // Medios de contacto
      this.mediosContacto = (dto.mediosContacto || []).map(m => ({
        medioId: m.medioId || null,
        tipoMedioId: m.tipoMedioId || null,
        descripcion: m.descripcion || '',
        isMinimized: false,
      }));

      // Información financiera
      this.informacionFinanciera = (dto.informacionFinanciera || []).map(f => ({
        informacionFinancieraId: f.informacionFinancieraId || null,
        instrumentoId: f.instrumentoId || null,
        bancoId: f.bancoId || null,
        cuenta: f.cuenta || '',
        isMinimized: false,
      }));

      // Laborales
      this.lugarTrabajoId = dto.lugarTrabajoId || '';
      this.organizacionId = dto.organizacionId || '';
      this.unidadId = dto.unidadId || '';
      this.referencia = dto.referencia || '';

      // Producto
      this.aseguradoraId = dto.aseguradoraId || '';
      this.productoId = dto.productoId || '';
      this.ramoId = dto.ramoId || '';
      this.plazoId = dto.plazoId || '';
      this.tipoId = dto.tipoId || '';
      this.divisaId = dto.divisaId || '';
      this.enrolerId = dto.enrolerId || '';
      this.fechaSolicitud = dto.fechaSolicitud || '';
      this.poliza = dto.numeroPoliza || '';
      this.notasOrdenDeTrabajo = dto.notas || '';
      this.observacionesInternas = dto.observaciones || '';
      this.coaseguro = dto.coaseguro ?? 0;
      this.topeCoaseguro = dto.topeCoaseguro ?? 0;
      this.deducible = dto.deducible || '';

      // Asegurados
      this.asegurados = (dto.asegurados || []).map(a => ({
        aseguradoId: a.aseguradoId || null,
        nombre: a.nombre || '',
        paterno: a.paterno || '',
        materno: a.materno || '',
        fechaNacimiento: a.fechaNacimiento || '',
        fechaAlta: a.fechaAlta || '',
        fechaFin: a.fechaFin || '',
        edad: a.edad || '',
        genero: a.genero || '',
        parentesco: a.parentesco || '',
        antiguedad: a.antiguedad || '',
        diasContratados: a.diasContratados || '',
        estadoId: a.estadoId || null,
        paisId: a.paisId || null,
      }));

      // Condiciones de cotización (JSON anidado)
      let cond = dto.condiciones;
      if (typeof cond === 'string') {
        try { cond = JSON.parse(cond); } catch { cond = null; }
      }
      if (cond) {
        this.inicioVigencia = cond.inicioVigencia || cond.fechaAlta || '';
        this.finVigencia = cond.finVigencia || cond.fechaBaja || '';
        this.sumaPrimas = cond.primaNeta ?? 0;
        this.montoIva = cond.iva ?? 0;
        this.primaTotal = cond.primaTotal ?? 0;
        this.periodoPago = cond.frecuenciaPagoId ? String(cond.frecuenciaPagoId) : '';
        this.primaPeriodica = cond.primaPeriodica ?? 0;
        this.diasContratados = cond.diasContratados ?? '';
        this.sumaAsegurada = cond.sumaAsegurada ?? 100000000;
        this.coaseguro = cond.coaseguro ?? 0;
        this.topeCoaseguro = cond.topeCoaseguro ?? 0;
        this.conversionGarantizada = cond.conversionGarantizada ?? null;
        this.coberturasSeleccionadas = cond.coberturasSeleccionadas ?? [];
        // Vida no tiene deducible: la suma asegurada elegida es `sumaAsegurada`. Las pólizas de Vida
        // guardadas antes de este cambio la llevaban en `deducible`; se usa como respaldo.
        if (cond['$type'] === 'vida') {
          this.sumaAsegurada = cond.sumaAsegurada ?? (dto.deducible || '');
          this.deducible = '';
        }
      }
    },
  }
});
